import { FormFeedBack, ModalBase, Select } from '@repo/ui';
import { GetAllEntitiesSummaryResponse } from 'domain/models/responses/entities';
import { NotFoundError } from 'domain/errors';
import { ApiKeysServiceInterface } from 'domain/services';
import { useFormik } from 'formik';
import { useState } from 'react';
import { Col, Form, Input, Label, Row } from 'reactstrap';
import * as Yup from 'yup';

interface IProps {
  isOpen: boolean;
  entitiesList: GetAllEntitiesSummaryResponse[];
  apiKeysService: ApiKeysServiceInterface;
  toggleModal: () => void;
  onCreated: () => void;
}

export const ModalCreateApiKey = ({
  isOpen,
  entitiesList,
  apiKeysService,
  toggleModal,
  onCreated,
}: IProps) => {
  const [loading, setLoading] = useState(false);

  const entitiesOptions = entitiesList.map((e) => ({
    label: e.name,
    value: e.id,
  }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      appName: '',
      institutions: [] as { label: string; value: string }[],
      active: true,
    },
    validationSchema: Yup.object({
      appName: Yup.string().required('Campo obrigatório'),
      institutions: Yup.array().min(1, 'Selecione ao menos uma instituição'),
    }),
    onSubmit: async (values, helpers) => {
      setLoading(true);
      try {
        await apiKeysService.createApiKey({
          appName: values.appName,
          institutionIds: values.institutions.map(
            (institution) => institution.value,
          ),
          active: values.active,
        });
        helpers.resetForm();
        toggleModal();
        onCreated();
      } catch (error) {
        if (error instanceof NotFoundError) {
          helpers.setFieldTouched('institutions', true, false);
          helpers.setFieldError('institutions', error.message);
          return;
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <ModalBase.Wrapper
      size="md"
      isOpen={isOpen}
      loading={loading}
      toggle={toggleModal}
      onClosed={formik.resetForm}
    >
      <ModalBase.Header toggle={toggleModal} withBorderBottom>
        Nova chave de API
      </ModalBase.Header>

      <Form autoComplete="off" onSubmit={formik.handleSubmit}>
        <ModalBase.Body>
          <Row>
            <Col md={12} className="mb-3">
              <Label htmlFor="apiKeyAppName" className="form-label">
                Nome
              </Label>
              <Input
                id="apiKeyAppName"
                type="text"
                name="appName"
                autoFocus
                className="form-control"
                placeholder="Ex: App Parceiro X"
                value={formik.values.appName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched.appName && !!formik.errors.appName}
              />
              <FormFeedBack
                error={formik.errors.appName}
                isInvalid={formik.touched.appName && !!formik.errors.appName}
              />
            </Col>

            <Col md={12} className="mb-3">
              <Label className="form-label">Instituições</Label>
              <Select
                isMulti
                isClearable
                placeholder="Selecione..."
                options={entitiesOptions}
                value={formik.values.institutions}
                onChange={(opts: any) =>
                  formik.setFieldValue('institutions', opts ?? [])
                }
              />
              <FormFeedBack
                error={formik.errors.institutions as string}
                isInvalid={
                  formik.touched.institutions && !!formik.errors.institutions
                }
              />
            </Col>

            <Col md={12}>
              <div className="form-check form-switch">
                <Input
                  id="createApiKeyActive"
                  type="checkbox"
                  name="active"
                  role="switch"
                  className="form-check-input"
                  checked={formik.values.active}
                  onChange={formik.handleChange}
                />
                <Label
                  className="form-check-label"
                  htmlFor="createApiKeyActive"
                >
                  {formik.values.active ? 'Ativo' : 'Inativo'}
                </Label>
              </div>
            </Col>
          </Row>
        </ModalBase.Body>

        <ModalBase.Footer>
          <ModalBase.Actions
            loading={loading}
            goBack={toggleModal}
            actionBtnType="submit"
            confirmBtnLabel="Criar chave"
          />
        </ModalBase.Footer>
      </Form>
    </ModalBase.Wrapper>
  );
};
